<template>
  <section class="section product-showcase">
    <div class="container">
      <div class="product-head">
        <div class="product-title-block">
          <div class="product-en">PRODUCTS</div>
          <h2>{{ titleText }}</h2>
          <p>{{ subtitleText }}</p>
        </div>

        <div class="product-tabs">
          <button
            v-for="item in items"
            :key="item.path"
            type="button"
            class="product-tab"
            :class="{ active: activePath === item.path }"
            @click="activePath = item.path"
          >
            <span class="tab-dot"></span>
            <span>{{ $pick(item.label) }}</span>
          </button>
        </div>
      </div>

      <div class="product-grid">
        <router-link
          v-for="card in displayCards"
          :key="card.path + card.title + card.imageUrl"
          v-reveal
          :to="card.path"
          class="product-card"
        >
          <div class="product-image" :style="productImageStyle(card)">
            <div class="product-image-overlay"></div>
          </div>
          <div class="product-card-body">
            <strong>{{ card.title }}</strong>
            <span>{{ detailText }}</span>
          </div>
        </router-link>
      </div>
    </div>
  </section>
</template>

<script>
import { productPageMap } from "../../data/site";

export default {
  name: "ProductMenuGrid",
  props: {
    items: Array
  },
  data() {
    return {
      activePath: "/products/oil-immersed"
    };
  },
  computed: {
    titleText() {
      return this.locale === "zh-CN" ? "产品中心" : "Product Center";
    },
    subtitleText() {
      return this.locale === "zh-CN" ? "匠心品质 · 专业服务" : "Craftsmanship and Quality · Professional Service";
    },
    detailText() {
      return this.locale === "zh-CN" ? "查看详情" : "View Details";
    },
    cardMap() {
      return Object.keys(productPageMap).reduce((map, categoryPath) => {
        map[categoryPath] = (productPageMap[categoryPath].images || []).map((image) => ({
          title: this.$pick(image.title),
          path: image.slug ? `/products/detail/${image.slug}` : categoryPath,
          imageUrl: image.src
        }));
        return map;
      }, {});
    },
    displayCards() {
      return this.cardMap[this.activePath] || this.cardMap["/products/oil-immersed"];
    }
  },
  methods: {
    productImageStyle(card) {
      if (!card.imageUrl) {
        return {};
      }

      return {
        backgroundImage: `url('${card.imageUrl}')`
      };
    }
  }
};
</script>

<style scoped>
.product-showcase {
  background: #fff;
}

.product-head {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 28px;
  margin-bottom: 34px;
}

.product-title-block {
  padding-right: 20px;
}

.product-en {
  margin-bottom: 6px;
  font-size: clamp(48px, 6.8vw, 78px);
  font-weight: 800;
  line-height: 0.95;
  letter-spacing: 0.02em;
  background: linear-gradient(90deg, #0f57a8 0%, #1a6fcb 48%, #4f64b8 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.product-title-block h2 {
  margin: 0 0 16px;
  font-size: 44px;
  color: #272727;
}

.product-title-block p {
  margin: 0;
  color: #a1a1a1;
  font-size: 16px;
}

.product-tabs {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 14px;
}

.product-tab {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 18px;
  border: 1px solid #d8e6f6;
  border-radius: 999px;
  background: linear-gradient(180deg, #f6fbff, #ffffff);
  color: #1b64b6;
  font-size: 15px;
  cursor: pointer;
  transition: transform 0.25s ease, color 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease;
}

.tab-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.7;
}

.product-tab:hover,
.product-tab.active {
  color: #ffffff;
  border-color: transparent;
  background: linear-gradient(135deg, #0f4f9d, #1a6fcb 58%, #4f64b8);
  box-shadow: 0 14px 28px rgba(26, 111, 203, 0.22);
  transform: translateY(-2px);
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 38px 32px;
}

.product-card {
  display: block;
  background: #fff;
}

.product-image {
  position: relative;
  height: 0;
  padding-bottom: 68%;
  overflow: hidden;
  background-color: #edf2f7;
  background-size: cover;
  background-position: center;
  transition: transform 0.45s ease, box-shadow 0.45s ease;
}

.product-image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.02), rgba(0, 0, 0, 0.12));
  transition: background-color 0.35s ease;
}

.product-card-body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 18px 10px 8px 10px;
}

.product-card-body strong {
  color: #2d2d2d;
  font-size: 21px;
  font-weight: 500;
  line-height: 1.35;
  transition: transform 0.35s ease, color 0.35s ease;
}

.product-card-body span {
  color: #c9ced9;
  font-size: 13px;
  opacity: 0;
  flex: 0 0 auto;
  transform: translateX(-10px);
  transition: opacity 0.35s ease, transform 0.35s ease, color 0.35s ease;
}

.product-card:hover .product-image {
  transform: translateY(-8px) scale(1.025);
  box-shadow: 0 24px 46px rgba(17, 46, 89, 0.16);
}

.product-card:hover .product-image-overlay {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.02), rgba(8, 40, 75, 0.2));
}

.product-card:hover .product-card-body strong {
  color: var(--brand-blue);
  transform: translateY(-3px);
}

.product-card:hover .product-card-body span {
  opacity: 1;
  color: #1a6fcb;
  transform: translateX(0);
}

@media (max-width: 900px) {
  .product-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .product-title-block h2 {
    font-size: 34px;
  }

  .product-tabs {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
    gap: 10px;
  }

  .product-tab {
    justify-content: flex-start;
    width: 100%;
    padding: 11px 12px;
    font-size: 14px;
  }
}

@media (max-width: 520px) {
  .product-head {
    gap: 24px;
    margin-bottom: 26px;
  }

  .product-title-block {
    padding-right: 0;
  }

  .product-en {
    font-size: 42px;
  }

  .product-title-block h2 {
    font-size: 28px;
  }

  .product-title-block p {
    font-size: 14px;
  }

  .product-tabs {
    grid-template-columns: 1fr;
  }

  .product-card-body {
    align-items: flex-start;
    padding: 14px 4px 0;
  }

  .product-card-body strong {
    font-size: 18px;
  }

  .product-card-body span {
    opacity: 1;
    transform: none;
  }
}
</style>
